/**
 * First-run onboarding wizard — the web counterpart of `OnboardingScreen.swift`.
 *
 * Shown INSTEAD of the login screen on a fresh deployment (server reports
 * `firstRun` while no password has been set), so a brand-new user never meets
 * a login wall:
 *
 *   Welcome → Set login password → Cloud storage → Done
 *
 * The password step comes first and is required: POST /auth/setup both stores
 * the credential and issues a session, which is what authorizes the storage
 * step (its save endpoint needs a signed-in caller). Storage is skippable;
 * the Done summary reports what ended up configured. Reaching Done marks
 * onboarding complete immediately (like the Mac app) so a closed tab never
 * replays the wizard.
 */
import { useEffect, useState } from 'react';
import { Cloud, KeyRound, NotebookText } from 'lucide-react';
import { settingsApi, type StorageSettingsView } from '../lib/api/settings.js';
import { StorageCredentialForm } from '../components/StorageCredentialForm.js';
import { PasswordChangeForm } from '../components/PasswordChangeForm.js';
import { markOnboardingDone } from '../lib/onboarding.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';

type Step = 'welcome' | 'password' | 'storage' | 'done';

const STEPS: Step[] = ['welcome', 'password', 'storage', 'done'];
/** Dot indicator covers the three setup steps; Done fills them all. */
const DOT_STEPS = STEPS.slice(0, 3);

interface OnboardingScreenProps {
  onFinished: () => void;
}

export function OnboardingScreen({ onFinished }: OnboardingScreenProps) {
  const [step, setStep] = useState<Step>('welcome');
  const [forward, setForward] = useState(true);
  const [passwordSet, setPasswordSet] = useState(false);
  const [storageConfigured, setStorageConfigured] = useState(false);
  /** Storage view for the credential form — only fetchable once signed in. */
  const [storageView, setStorageView] = useState<StorageSettingsView | null>(null);
  const [storageViewLoaded, setStorageViewLoaded] = useState(false);
  // Floating capsule scrollbar (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  // The stage remounts per step (key), so the scroller ref detaches/reattaches.
  const stageScroll = useOverlayScrollbar<HTMLDivElement>();

  // Persist completion the moment Done appears — a force-quit here must not
  // replay the wizard (same rationale as the macOS onDone appear hook).
  useEffect(() => {
    if (step === 'done') markOnboardingDone();
  }, [step]);

  // The storage settings endpoint needs the session the password step issued,
  // so the view is fetched when the storage step is entered, not up front.
  useEffect(() => {
    if (step !== 'storage' || storageViewLoaded) return;
    let cancelled = false;
    void settingsApi
      .getStorage()
      .then((result) => {
        if (cancelled) return;
        setStorageView(result.storage);
        setStorageConfigured(result.storage.configured);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setStorageViewLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [step, storageViewLoaded]);

  function go(next: Step): void {
    setForward(STEPS.indexOf(next) > STEPS.indexOf(step));
    setStep(next);
  }

  const index = STEPS.indexOf(step);
  const previous = STEPS[index - 1];

  const primaryLabel =
    step === 'welcome' ? 'Continue' : step === 'storage' ? 'Finish' : 'Start using Panda Note';

  // Hero steps keep the scene bright (0.22 scrim); form steps darken the
  // left band further (0.42) so inputs stay legible — same values as the
  // macOS wizard's backdrop scrim.
  const scrim = step === 'welcome' || step === 'done' ? 'hero' : 'form';

  return (
    <div className="onboarding" data-scrim={scrim}>
      <div className="ob-host ob-host-grow" ref={stageScroll.hostRef}>
        <div
          className="onboarding-stage"
          key={step}
          data-direction={forward ? 'forward' : 'backward'}
          ref={stageScroll.scrollerRef}
        >
        {step === 'welcome' && (
          <section className="onboarding-panel">
            <h1 className="onboarding-title">Welcome to Panda Note</h1>
            <p className="onboarding-text">
              Let's take a minute to set up your password and cloud sync.
              Sync is optional now and can be changed anytime in Settings.
            </p>
            <div className="onboarding-rows">
              <IntroRow
                icon={<KeyRound size={15} strokeWidth={1.75} />}
                title="Login password"
                subtitle="Choose the password you'll use to sign in from now on."
              />
              <IntroRow
                icon={<Cloud size={15} strokeWidth={1.75} />}
                title="Cloud storage"
                subtitle="S3-compatible backend that syncs your notes."
              />
            </div>
          </section>
        )}

        {step === 'password' && (
          <section className="onboarding-panel onboarding-panel-wide">
            <StepTitle
              title="Set your login password"
              subtitle="This is a fresh Panda Note server — no password has been set yet. The one you choose here replaces the server's default and takes effect immediately."
            />
            <div className="onboarding-card">
              <PasswordChangeForm
                setup
                minLength={4}
                submitLabel="Set password and continue"
                description="You stay signed in on this device once the password is set."
                onChanged={() => {
                  setPasswordSet(true);
                  go('storage');
                }}
              />
            </div>
            <p className="onboarding-hint">The password is required to continue.</p>
          </section>
        )}

        {step === 'storage' && (
          <section className="onboarding-panel onboarding-panel-wide">
            <StepTitle
              title="Connect cloud storage"
              subtitle="Your notes sync through an S3-compatible bucket. Credentials stay on the server and take effect immediately. You can skip this and configure it later in Settings."
            />
            <div className="onboarding-card">
              {storageViewLoaded ? (
                <StorageCredentialForm
                  initial={storageView}
                  saveLabel="Save and continue"
                  onSaved={(view) => {
                    setStorageConfigured(view.configured);
                    go('done');
                  }}
                />
              ) : (
                <div className="onboarding-loading">Loading…</div>
              )}
            </div>
          </section>
        )}

        {step === 'done' && (
          <section className="onboarding-panel">
            <div className="onboarding-brand">
              <NotebookText size={22} strokeWidth={1.75} />
            </div>
            <h1 className="onboarding-title">All set</h1>
            <p className="onboarding-text">You can change either of these anytime in Settings.</p>
            <div className="onboarding-rows">
              <SummaryRow
                icon={<KeyRound size={15} strokeWidth={1.75} />}
                title="Login password"
                status={passwordSet ? 'Your own password' : 'Not set'}
                ready={passwordSet}
              />
              <SummaryRow
                icon={<Cloud size={15} strokeWidth={1.75} />}
                title="Cloud storage"
                status={storageConfigured ? 'Enabled' : 'Not set up'}
                ready={storageConfigured}
              />
            </div>
          </section>
        )}
        </div>
      </div>

      <div className="onboarding-footer">
        {step === 'welcome' ? (
          <button type="button" className="button onboarding-primary" onClick={() => go('password')}>
            Continue
          </button>
        ) : step === 'password' ? (
          // Required step: only Back is offered; the form's submit advances.
          previous && (
            <button
              type="button"
              className="button button-quiet onboarding-secondary"
              onClick={() => go(previous)}
            >
              Back
            </button>
          )
        ) : step === 'storage' ? (
          <>
            {previous && (
              <button
                type="button"
                className="button button-quiet onboarding-secondary"
                onClick={() => go(previous)}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="button button-quiet onboarding-secondary"
              onClick={() => go('done')}
            >
              Skip this step
            </button>
            <button type="button" className="button onboarding-primary" onClick={() => go('done')}>
              {primaryLabel}
            </button>
          </>
        ) : (
          <button type="button" className="button onboarding-primary" onClick={onFinished}>
            {primaryLabel}
          </button>
        )}
      </div>

      <div className="onboarding-dots" role="presentation">
        {DOT_STEPS.map((dotStep, dotIndex) => (
          <span
            key={dotStep}
            className={
              step === 'done' || dotIndex <= index
                ? 'onboarding-dot onboarding-dot-on'
                : 'onboarding-dot'
            }
          />
        ))}
      </div>
    </div>
  );
}

function StepTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="onboarding-step-head">
      <h2 className="onboarding-subtitle">{title}</h2>
      <p className="onboarding-text">{subtitle}</p>
    </div>
  );
}

function IntroRow({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="onboarding-row">
      <span className="onboarding-row-icon onboarding-row-icon-accent">{icon}</span>
      <div className="onboarding-row-copy">
        <span className="onboarding-row-title">{title}</span>
        <span className="onboarding-row-subtitle">{subtitle}</span>
      </div>
    </div>
  );
}

function SummaryRow({
  icon,
  title,
  status,
  ready,
}: {
  icon: React.ReactNode;
  title: string;
  status: string;
  ready: boolean;
}) {
  return (
    <div className="onboarding-row">
      <span
        className={
          ready ? 'onboarding-row-icon onboarding-row-icon-accent' : 'onboarding-row-icon'
        }
      >
        {icon}
      </span>
      <div className="onboarding-row-copy">
        <span className="onboarding-row-title">{title}</span>
        <span
          className={
            ready ? 'onboarding-row-status onboarding-row-status-on' : 'onboarding-row-status'
          }
        >
          {status}
        </span>
      </div>
    </div>
  );
}
